import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { MessageCircle } from 'lucide-react';
import { EmployeePopover } from '../ui/employee-popover';
import { GET_PROJECT_COLUMNS, GET_PROJECT_TASKS } from '../../graphql/queries';
import { TaskCardData } from './TaskCard';
import { TaskDialogTask } from './TaskDialog';
import {
  TASK_PRIORITY_CLASSES,
  TASK_PRIORITY_LABELS,
  formatDate,
  getDueDateColorClass,
  getInitials,
} from './projectUtils';

interface ProjectTaskRowsProps {
  projectId: string;
  onOpenTask: (
    projectId: string,
    task: TaskDialogTask,
    columns: Array<{ id: string; name: string }>,
  ) => void;
}

interface ColumnRef {
  id: string;
  name: string;
  order: number;
}

export const ProjectTaskRows: React.FC<ProjectTaskRowsProps> = ({
  projectId,
  onOpenTask,
}) => {
  const { data: columnsData } = useQuery<{
    projectColumns: ColumnRef[];
  }>(GET_PROJECT_COLUMNS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: tasksData, loading } = useQuery<{
    projectTasks: TaskCardData[];
  }>(GET_PROJECT_TASKS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const columnMap = useMemo(() => {
    const map: Record<string, { name: string; order: number }> = {};
    (columnsData?.projectColumns || []).forEach((c) => {
      map[c.id] = { name: c.name, order: c.order };
    });
    return map;
  }, [columnsData]);

  const rows = useMemo(() => {
    const tasks = tasksData?.projectTasks || [];
    return [...tasks].sort((a, b) => {
      const oa = columnMap[a.columnId]?.order ?? 999;
      const ob = columnMap[b.columnId]?.order ?? 999;
      if (oa !== ob) return oa - ob;
      return a.orderInColumn - b.orderInColumn;
    });
  }, [tasksData, columnMap]);

  const columnsForDialog = useMemo(
    () =>
      (columnsData?.projectColumns || []).map((c) => ({
        id: c.id,
        name: c.name,
      })),
    [columnsData],
  );

  let content: React.ReactNode;
  if (loading && rows.length === 0) {
    content = (
      <div className="py-4 flex justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  } else if (rows.length === 0) {
    content = (
      <div className="py-3 text-sm text-center text-muted-foreground italic">
        Brak zadań w tym projekcie
      </div>
    );
  } else {
    content = (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 text-xs uppercase text-muted-foreground tracking-wide">
              <th className="py-1.5 px-3 text-left font-medium w-[40%]">Zadanie</th>
              <th className="py-1.5 px-3 text-center font-medium">Status</th>
              <th className="py-1.5 px-3 text-center font-medium">Priorytet</th>
              <th className="py-1.5 px-3 text-center font-medium">Przypisani</th>
              <th className="py-1.5 px-3 text-center font-medium">Termin</th>
              <th className="py-1.5 px-3 text-center font-medium">Komentarze</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const colName = columnMap[t.columnId]?.name || '—';
              const assignees = t.assignees || [];
              const visible = assignees.slice(0, 3);
              const overflow = Math.max(0, assignees.length - 3);
              const dueColor = getDueDateColorClass(t.plannedEndDate);

              return (
                <tr
                  key={t.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenTask(
                      projectId,
                      {
                        id: t.id,
                        name: t.name,
                        description: t.description,
                        priority: t.priority,
                        startDate: t.startDate,
                        plannedEndDate: t.plannedEndDate,
                        columnId: t.columnId,
                        assignees: t.assignees || [],
                      },
                      columnsForDialog,
                    );
                  }}
                  className="border-b border-border/40 last:border-b-0 hover:bg-background/60 cursor-pointer"
                >
                  <td className="py-1.5 px-3 truncate max-w-0" title={t.name}>
                    <span className="font-medium">{t.name}</span>
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground tracking-wide whitespace-nowrap">
                      {colName}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    <span
                      className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                        TASK_PRIORITY_CLASSES[t.priority] || ''
                      }`}
                    >
                      {TASK_PRIORITY_LABELS[t.priority] || t.priority}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    {assignees.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">
                        nieprzypisane
                      </span>
                    ) : (
                      <div className="flex -space-x-1.5 justify-center">
                        {visible.map((a) => {
                          const emp = a.employee;
                          const avatar = (
                            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold flex items-center justify-center border border-background">
                              {getInitials(emp?.firstName, emp?.lastName)}
                            </div>
                          );
                          if (!emp) return <div key={a.id}>{avatar}</div>;
                          return (
                            <EmployeePopover
                              key={a.id}
                              employeeId={emp.id}
                              fallbackName={`${emp.firstName} ${emp.lastName}`}
                            >
                              {avatar}
                            </EmployeePopover>
                          );
                        })}
                        {overflow > 0 && (
                          <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold flex items-center justify-center border border-background">
                            +{overflow}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td
                    className={`py-1.5 px-3 text-center text-xs whitespace-nowrap ${dueColor}`}
                  >
                    {formatDate(t.plannedEndDate)}
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    {(t.commentsCount ?? 0) > 0 ? (
                      <div className="relative inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>{t.commentsCount}</span>
                        {(t.unreadCommentsCount ?? 0) > 0 && (
                          <span
                            className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-[14px] text-center"
                            title={`${t.unreadCommentsCount} nieprzeczytanych`}
                          >
                            {t.unreadCommentsCount}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return <>{content}</>;
};
